import { FunctionComponent, useEffect, useState } from 'react'
import styles from './childsTable.module.scss'
import { Table, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { RegularSlot } from '@/types/Slots'
import { Daycare } from '@/types/Daycare'
import { Section } from '@/types/Section'
import SectionsTableRow from './sectionsTableRow'
import { useTranslation } from 'react-i18next'
import { ChildDaycareWithParents, ChildSection, ChildWithParents } from '@/types/Child'
import Desktop from '@/modules/desktopMobile/desktop'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'

interface ChildsTableProps {
    childList: ChildWithParents[]
    daycare: Daycare | undefined
    sectionsList: Section[]
    canWriteRegular: boolean
    canReadRegular: boolean
    regularSlots: RegularSlot[]
    selectedChildIds: number[]
    onToggleChild: (childId: number) => void
    onToggleSection: (childIds: number[], select: boolean) => void
    onToggleAll: (select: boolean) => void
}

type SectionAndChilds = {
    section: Section
    childList: ChildWithParents[]
}

const ChildsTable: FunctionComponent<ChildsTableProps> = ({
    childList,
    daycare,
    sectionsList,
    canWriteRegular,
    canReadRegular,
    regularSlots,
    selectedChildIds,
    onToggleChild,
    onToggleSection,
    onToggleAll
}: ChildsTableProps) => {
    const { t } = useTranslation()

    function initSectionsAndChilds(sections: Section[], childs: ChildWithParents[]): SectionAndChilds[] {
        const availableSections: Section[] = sections.filter((section: Section): ChildWithParents | undefined => {
            return childs.find((child: ChildWithParents): ChildDaycareWithParents | undefined =>
                child.daycares.find((daycare: ChildDaycareWithParents): boolean =>
                    daycare.sections.some((s: ChildSection) => s.sectionId === section.id && s.isPrimary)
                )
            )
        })

        const res: SectionAndChilds[] = []

        availableSections.map((section: Section) => {
            const childsInSection: ChildWithParents[] = childs.filter(
                (child: ChildWithParents): ChildDaycareWithParents | undefined =>
                    child.daycares.find((daycare: ChildDaycareWithParents): boolean =>
                        daycare.sections.some((s: ChildSection) => s.sectionId === section.id && s.isPrimary)
                    )
            )
            res.push({ section: section, childList: childsInSection })
        })

        return res
    }

    const [sectionsAndChilds, setSectionsAndChilds] = useState<SectionAndChilds[]>(
        initSectionsAndChilds(sectionsList, childList)
    )

    const [oneChildHaveSoftwareId, setOneChildHaveSoftwareId] = useState<boolean>(
        childList.some((child: ChildWithParents): boolean =>
            child.daycares.some(
                (d: ChildDaycareWithParents): boolean => d.daycareId === daycare?.id && d.softwareId !== null
            )
        )
    )

    useEffect(() => {
        setSectionsAndChilds(initSectionsAndChilds(sectionsList, childList))

        setOneChildHaveSoftwareId(
            childList.some(
                (child: ChildWithParents): boolean =>
                    child.daycares.find(
                        (d: ChildDaycareWithParents): boolean => d.daycareId === daycare?.id && d.softwareId !== null
                    ) !== undefined
            )
        )
    }, [sectionsList, childList])

    return (
        <>
            {childList.length > 0 ? (
                <TableContainer className={styles.TablePlacement}>
                    <Table className={styles.Table}>
                        <TableHead className={styles.TableHeader}>
                            <TableRow>
                                <TableCell>
                                    <Checkbox
                                        checked={
                                            selectedChildIds.length > 0 && selectedChildIds.length === childList.length
                                        }
                                        indeterminate={
                                            selectedChildIds.length > 0 && selectedChildIds.length < childList.length
                                        }
                                        onChange={onToggleAll}
                                        className={styles.Checkbox}
                                    />
                                </TableCell>
                                <Desktop>
                                    {canReadRegular && <TableCell align={'center'}>{t('regularSlots')}</TableCell>}
                                    <TableCell align={'center'}>{t('numberAdults')}</TableCell>
                                    {oneChildHaveSoftwareId && (
                                        <TableCell align={'center'}>{t('softwareId')}</TableCell>
                                    )}
                                    <TableCell />
                                </Desktop>
                            </TableRow>
                        </TableHead>
                        {sectionsAndChilds.map((section: SectionAndChilds, key: number, list: SectionAndChilds[]) => (
                            <SectionsTableRow
                                childList={section.childList}
                                daycare={daycare}
                                section={section.section.name}
                                regularSlots={regularSlots}
                                oneChildHaveSoftwareId={oneChildHaveSoftwareId}
                                key={
                                    key.toString() + section.section.id.toString() + section.childList.length.toString()
                                }
                                canWriteRegular={canWriteRegular}
                                canReadRegular={canReadRegular}
                                openSection={list.length === 1}
                                selectedChildIds={selectedChildIds}
                                onToggleChild={onToggleChild}
                                onToggleSection={onToggleSection}
                            />
                        ))}
                        {childList.find(
                            (child: ChildWithParents): boolean =>
                                child.daycares[
                                    child.daycares.findIndex(
                                        (d: ChildDaycareWithParents): boolean => d.daycareId === daycare?.id
                                    )
                                ]?.sections.length === 0
                        ) && (
                            <SectionsTableRow
                                childList={[
                                    ...childList.filter(
                                        (child: ChildWithParents): boolean =>
                                            child.daycares[
                                                child.daycares.findIndex(
                                                    (d: ChildDaycareWithParents): boolean => d.daycareId === daycare?.id
                                                )
                                            ]?.sections.length === 0
                                    )
                                ]}
                                oneChildHaveSoftwareId={oneChildHaveSoftwareId}
                                daycare={daycare}
                                section={null}
                                regularSlots={regularSlots}
                                openSection={true}
                                canWriteRegular={canWriteRegular}
                                canReadRegular={canReadRegular}
                                selectedChildIds={selectedChildIds}
                                onToggleChild={onToggleChild}
                                onToggleSection={onToggleSection}
                            />
                        )}
                    </Table>
                </TableContainer>
            ) : (
                <div className={styles.NoContracts}>
                    <p> {t('noChilds')} </p>
                </div>
            )}
        </>
    )
}

export default ChildsTable
